import { useMemo } from 'react';

import { formatPrice } from '@/services/formatter';
import { checkIsValidJSON } from '@/services/helpers/checkIsValidJSON';

import { getBaseAdditionalProducts } from '../../../helpers/getBaseAdditionalProducts';
import type {
  IEurasAlternativeFromResponseApprovedBody,
  IEurasParsedResponseApprovedBody,
  TEurasV2BankOffer
} from '../../../types/euras-v2.types';

interface IProps {
  bankOffer: TEurasV2BankOffer;
}

const useAlternatives = ({ bankOffer }: IProps) => {
  const hasAlternatives = Boolean(
    bankOffer.alternatives && bankOffer.alternatives?.length > 0
  );

  // Поддержка ответа от банка, когда у него нет Responses_Alternatives
  const eurasBankData: IEurasAlternativeFromResponseApprovedBody[] =
    useMemo(() => {
      const responseApprovedBody = bankOffer.responseApprovedBody;
      if (
        !responseApprovedBody ||
        !checkIsValidJSON(responseApprovedBody) ||
        hasAlternatives
      )
        return [];

      const parsedEurasData: IEurasParsedResponseApprovedBody[] =
        JSON.parse(responseApprovedBody);
      if (!Array.isArray(parsedEurasData)) return [];

      return parsedEurasData
        .map((item) => {
          const products: string[] = [];
          if (item?.casco) {
            const casco = item.casco;
            products.push(
              `${casco.name} ${casco.amount ? ` | ${formatPrice(casco.amount)} ₸` : ''} ${casco.term ? ` | ${casco.term} м.` : ''}`.trim()
            );
          }
          if (item?.additionalServices) {
            item?.additionalServices.forEach((item) => {
              products.push(
                `${item.name} ${item.amount ? ` | ${formatPrice(item.amount)} ₸` : ''}`.trim()
              );
            });
          }

          const isPaymentsSame =
            item.secondMonthlyPayment === item.firstMonthlyPayment;
          return {
            id: item.id,
            monthlyPayment: item.secondMonthlyPayment?.toLocaleString('ru-RU'),
            firstMonthlyPayment: isPaymentsSame
              ? undefined
              : item.firstMonthlyPayment?.toLocaleString('ru-RU'),
            loanAmount: item.loanAmount?.toLocaleString('ru-RU'),
            initialPayment: item.downPayment?.toLocaleString('ru-RU'),
            loanPeriod: item.loanTerm,
            rate: item.nominalRates?.[0].rate,
            insuranceName: item.casco?.name,
            insuranceAmount: item.casco?.amount,
            insuranceTerm: item.casco?.term,
            products,
            initialProducts: {
              casco: item.casco,
              additionalServices: item.additionalServices
            },
            mainProduct: item.tariffDescription
          };
        })
        .sort((a) => (a.id === bankOffer.offeredUuid ? -1 : 1));
    }, [
      bankOffer.responseApprovedBody,
      hasAlternatives,
      bankOffer.offeredUuid
    ]);

  const alternatives = useMemo(() => {
    if (!hasAlternatives) {
      return eurasBankData
        ? eurasBankData.map((item) => ({
            additionalProducts: getBaseAdditionalProducts(item),
            additionalConditions: undefined,
            extUuid: item.id, // Используется для отправки в банки
            firstMonthlyPayment: item.firstMonthlyPayment,
            initialPayment: item.initialPayment,
            insuranceAmount: item.insuranceAmount,
            insuranceName: item.insuranceName,
            insuranceTerm: item.insuranceTerm,
            loanAmount: item.loanAmount,
            loanPeriod: String(item.loanPeriod),
            monthlyPayment: item.monthlyPayment,
            product: item.mainProduct,
            rate: item.rate,
            responseUuid: bankOffer.id,
            uuid: item.id // Внутренний uuid
          }))
        : [];
    }

    return (bankOffer.alternatives || []).map((item) => {
      // По просьбе банка, используем secondMonthlyPayment для monthlyPayment
      // как основное значение, а firstMonthlyPayment для первого платежа
      const description =
        typeof item.description !== 'string' ? item.description : undefined;
      const isPaymentsSame =
        description?.secondMonthlyPayment === description?.firstMonthlyPayment;
      return {
        ...item,
        loanPeriod:
          description?.nominalRates && description.nominalRates.length > 1
            ? `${description.nominalRates[0].fromMonth} - ${description.nominalRates[0].tillMonth}`
            : item.loanPeriod,
        monthlyPayment:
          description?.secondMonthlyPayment?.toLocaleString('ru-RU') ||
          item.monthlyPayment,
        firstMonthlyPayment: isPaymentsSame
          ? undefined
          : description?.firstMonthlyPayment?.toLocaleString('ru-RU'),
        additionalProducts: getBaseAdditionalProducts(item),
        additionalConditions: description?.nominalRates?.[1]
          ? {
              rate: description.nominalRates[1].rate,
              loanPeriod: `${description.nominalRates[1].fromMonth} - ${description.nominalRates[1].tillMonth}`,
              monthlyPayment:
                description.secondRateMonthlyPayment?.toLocaleString('ru-RU') ||
                ''
            }
          : undefined
      };
    });
  }, [bankOffer.alternatives, hasAlternatives, eurasBankData, bankOffer.id]);

  return { alternatives };
};

export default useAlternatives;
